import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Briefcase, Calendar, Loader2 } from "lucide-react";
import { ProgressSteps } from "./Freelancer/ProgressSteps";
import { FreelancerPersonalForm } from "./Freelancer/FreelancerPersonalForm";
import { FreelancerProfessionalForm } from "./Freelancer/FreelancerProfessionalForm";
import { FreelancerSkillsForm } from "./Freelancer/FreelancerSkillsForm";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/utils/routes";
import { useUserStore } from "@/store/userStore";
import { isValidPhoneNumber } from "react-phone-number-input";
import { ACCEPTED_FILE_TYPES, MAX_RESUME_FILE_SIZE } from "@/utils/constant";
import { useFreelancerStore } from "@/store/freelancerStore";
import LoaderOverlay from "../common/LoaderOverlay";
import { handleLogout } from "@/utils/logout";
import { format } from "date-fns";
import AILoader from "../common/AILoader";

interface FreelancerDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  setIsAIDetails: (value: boolean) => void;
  initialData?: z.infer<typeof fullSchema>;
}

const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/;

const educationSchema = z.object({
  degree: z.string().min(2, "Degree is required"),
  institution: z.string().min(3, "Institution name is required"),
  year: z
    .any()
    .transform((val) => (val == null ? "" : val)) // convert null to empty string
    .refine((val) => typeof val === "string" && val.trim() !== "", {
      message: "Passing year is nvalid date",
    }),
});

const workExperienceSchema = z.object({
  title: z.string().min(3, "Job title is required"),
  company: z.string().min(3, "Company name is required"),
  responsibilities: z.array(z.string().optional()).optional(),
  dates: z
    .object({
      start: z.preprocess(
        (val) => (val === null || val === undefined ? "" : val),
        z.string()
      ),
      end: z.preprocess(
        (val) => (val === null || val === undefined ? "" : val),
        z.string()
      ),
    })
    .superRefine((data, ctx) => {
      const { start, end } = data;

      const hasStart = !!start.trim();
      const hasEnd = !!end.trim();

      // Case: Both empty → show overall error
      if (!hasStart && !hasEnd) {
        ctx.addIssue({
          path: [],
          code: z.ZodIssueCode.custom,
          message: "Project duration is invalid",
        });
        return;
      }

      // Case: Only end is missing
      if (hasStart && !hasEnd) {
        ctx.addIssue({
          path: ["end"],
          code: z.ZodIssueCode.custom,
          message: "End date is required",
        });
        return;
      }

      // Case: Start or end is invalid or end < start
      if (hasStart && hasEnd) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          ctx.addIssue({
            path: [],
            code: z.ZodIssueCode.custom,
            message: "Invalid date format",
          });
          return;
        }

        if (startDate > endDate) {
          ctx.addIssue({
            path: ["end"],
            code: z.ZodIssueCode.custom,
            message: "End date must be after start date",
          });
        }
      }
    }),
});

const projectSchema = z.object({
  name: z.string().min(3, "Project name is required"),
  description: z.string().min(3, "Project description is required"),
  technologiesUsed: z
    .array(z.string())
    .min(1, "At least one technology is required"),
  role: z.string().min(2, "Role is required"),
  projectDuration: z
    .object({
      start: z.preprocess(
        (val) => (val === null || val === undefined ? "" : val),
        z.string()
      ),
      end: z.preprocess(
        (val) => (val === null || val === undefined ? "" : val),
        z.string()
      ),
    })
    .superRefine((data, ctx) => {
      const { start, end } = data;

      const hasStart = !!start.trim();
      const hasEnd = !!end.trim();

      // Case: Both empty → show overall error
      if (!hasStart && !hasEnd) {
        ctx.addIssue({
          path: [],
          code: z.ZodIssueCode.custom,
          message: "Project duration is invalid",
        });
        return;
      }

      // Case: Only end is missing
      if (hasStart && !hasEnd) {
        ctx.addIssue({
          path: ["end"],
          code: z.ZodIssueCode.custom,
          message: "End date is required",
        });
        return;
      }

      // Case: Start or end is invalid or end < start
      if (hasStart && hasEnd) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
          ctx.addIssue({
            path: [],
            code: z.ZodIssueCode.custom,
            message: "Invalid date format",
          });
          return;
        }

        if (startDate > endDate) {
          ctx.addIssue({
            path: ["end"],
            code: z.ZodIssueCode.custom,
            message: "End date must be after start date",
          });
        }
      }
    }),
});

// Full schema for final submission
const fullSchema = z.object({
  userId: z.string().optional(),
  role: z.string().optional(),
  email: z.string().optional(),
  accessToken: z.string().optional(),
  name: z.string().optional(),
  location: z.string().min(2, "Location is required"),
  phone: z
    .string()
    .min(1, "Phone number is required")
    .refine((value) => value && isValidPhoneNumber(value), {
      message: "Invalid phone number format",
    }),
  bio: z
    .string()
    .min(10, "Bio must be at least 10 characters")
    .max(2000, "Bio must not exceed 2000 characters"),
  designation: z.string().min(2, "designation is required"),
  category: z.string().min(1, "Category is required"),
  experience: z.preprocess(
    (val) => {
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z
      .number({
        required_error: "Invalid number of experience",
        invalid_type_error: "Invalid number of experience",
      })
      .min(0, "Experience must be 0 or more")
  ),
  workExperience: z.array(workExperienceSchema).optional(),
  projects: z.array(projectSchema).optional(),
  projectsCompleted: z.preprocess(
    (val) => {
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z
      .number({
        required_error: "Invalid project completed",
        invalid_type_error: "Invalid project completed",
      })
      .min(0, "Project completed must be greater than 0")
  ),
  dailyRate: z.preprocess(
    (val) => {
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z
      .number({
        required_error: "Invalid daily rate",
        invalid_type_error: "Invalid daily rate",
      })
      .gt(0, "Daily rate must be greater than 0")
  ),
  skills: z.array(z.string()).min(1, "At least one skill is required"),
  certifications: z.array(z.string()).optional(),
  resume_url: z
    .any()
    .optional(),
  // .refine((file) => !file || file instanceof File, {
  //   message: "Invalid file input.",
  // })
  // .refine((file) => !file || ACCEPTED_FILE_TYPES.includes(file.type), {
  //   message: "Only .pdf or .doc/.docx files are allowed.",
  // })
  // .refine((file) => !file || file.size <= MAX_RESUME_FILE_SIZE, {
  //   message: "File size must be 5MB or less.",
  // }),
  achievements: z.array(z.string()).optional(),
  socialMediaLinks: z
    .array(
      z.string().refine((value) => urlRegex.test(value), {
        message: "Invalid URL format",
      })
    )
    .optional(),
  socialMediaLinksArray: z
    .array(
      z.record(
        z.string().refine((value) => urlRegex.test(value), {
          message: "Invalid URL format",
        })
      )
    )
    .optional(),
  education: z.array(educationSchema).optional(),
});

type FormData = z.infer<typeof fullSchema>;

export const FreelancerDetailModal = ({
  isOpen,
  onClose,
  initialData,
  setIsAIDetails
}: FreelancerDetailModalProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { login } = useUserStore();
  const { user } = useUserStore();
  const { createFreelancerProfile, isLoading: loader } = useFreelancerStore();
  const [isLoading, setIsLoading] = useState(false);

  const methods = useForm<FormData>({
    resolver: zodResolver(fullSchema),
    defaultValues: {
      userId: initialData?.userId || "",
      name: initialData?.name || "",
      email: initialData?.email || "",
      accessToken: initialData?.accessToken || "",
      phone: "",
      location: "",
      bio: "",
      designation: "",
      category: "",
      experience: 0,
      projectsCompleted: 0,
      dailyRate: 1,
      skills: [],
      certifications: [],
      education: [],
      resume_url: null,
      achievements: [],
      socialMediaLinks: [],
      socialMediaLinksArray: [],
      workExperience: [],
      projects: []
    },
    mode: "onChange",
  });

  const { handleSubmit, trigger, setError, getValues } = methods;

  const stepFields = {
    1: ["resume_url", "location", "phone", "bio"],
    2: [
      "designation",
      "category",
      "experience",
      "workExperience",
      "projects",
      "projectsCompleted",
      "dailyRate",
    ],
    3: [
      "skills",
      "certifications",
      "achievements",
      "education",
      "socialMediaLinks",
    ],
    4: [],
  };

  const handleNext = async () => {
    const isValid = await trigger(stepFields[currentStep] as any);
    if (isValid) {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  const handlePrevious = () => {
    setCurrentStep((prev) => Math.max(prev - 1, 1));
  };

  const onSubmit = async (data: FormData) => {
    const payload = {
      ...data,
      socialMediaLinks: Array.isArray(data.socialMediaLinks)
        ? data.socialMediaLinks.map((link) => ({ _id: link.trim() }))
        : [],
      userId: initialData?.userId || data.userId,
      certifications: data.certifications
        ? data.certifications.filter((item) => item.trim() !== "")
        : [],
      achievements: data.achievements
        ? data.achievements.filter((item) => item.trim() !== "")
        : [],
      accessToken: data?.accessToken ? data?.accessToken : user?.accessToken,
      workExperience: data.workExperience.filter((entry) => {
        return (
          entry.title?.trim() ||
          entry.company?.trim() ||
          format(entry.dates?.start, "MMM-yyyy")?.trim() ||
          format(entry.dates?.end, "MMM-yyyy")?.trim() ||
          (entry.responsibilities &&
            entry.responsibilities.some((resp) => resp?.trim()))
        );
      }),
      education: data.education
        .filter((entry) => {
          return (
            entry.degree?.trim() ||
            entry.institution?.trim() ||
            entry.year?.trim()
          );
        })
        .map((entry) => ({
          degree: entry.degree || "",
          institution: entry.institution || "",
          year: entry.year || "",
        })),
      project: data.projects.filter((project) => {
        return (
          project.name?.trim() ||
          project.description?.trim() ||
          (project.technologiesUsed &&
            project.technologiesUsed.some((tech) => tech?.trim())) ||
          project.role?.trim() ||
          (project.projectDuration?.start && project.projectDuration?.end)
        );
      }),
    };
    if (!payload.resume_url) {
      setIsAIDetails(false)
    }

    try {
      const token = data?.accessToken ? data?.accessToken : user?.accessToken;
      const response = await createFreelancerProfile(payload, token);
      onClose();

      login({
        email: initialData.email || data.email,
        name: initialData?.name,
        userId: response.data.userId,
        role: initialData.role || data.role,
        isPremium: true,
        accessToken: initialData?.accessToken || response.data.accessToken,
        isDetailsSaved: true,
      });

      localStorage.removeItem("userDataResponse");

      setTimeout(() => {
        navigate(ROUTES.PROFILE);
      }, 1000)
    } catch (error) {
      handleLogout();
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={() => { }}>
        <DialogContent className="max-w-3xl p-0 rounded-xl overflow-hidden">
          <div className="max-h-[90vh] overflow-y-auto p-6">
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                  <Briefcase className="w-5 h-5 text-purple-500" />
                  Freelancer Profile Setup
                </DialogTitle>
              </div>
              <DialogDescription className="text-gray-600 flex items-center gap-2 mt-1">
                <Calendar className="w-4 h-4" />
                Complete your profile in a few steps
              </DialogDescription>
            </DialogHeader>

            <ProgressSteps currentStep={currentStep} />

            <FormProvider {...methods}>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  if (currentStep === 4) {
                    handleSubmit(onSubmit)(e);
                  }
                }}
                className="space-y-6"
              >
                {currentStep === 1 && (
                  <FreelancerPersonalForm setIsLoading={setIsLoading} />
                )}
                {currentStep === 2 && <FreelancerProfessionalForm />}
                {currentStep === 3 && <FreelancerSkillsForm />}

                <div className="flex justify-between mt-6">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevious}
                    disabled={currentStep === 1}
                    className="border-blue-100"
                  >
                    Previous
                  </Button>
                  <div className="flex gap-2">
                    {currentStep < 4 ? (
                      <Button
                        type="button"
                        onClick={handleNext}
                        disabled={isLoading}
                        className="bg-emerald-500 hover:bg-emerald-600"
                      >
                        {isLoading && (
                          <div className="flex justify-center items-center py-12">
                            <Loader2 className="w-24 h-24 text-white-700 animate-spin" />
                          </div>
                        )}
                        {currentStep === 3 ? "Submit" : "Next"}
                      </Button>
                    ) : (
                      <Button
                        type="submit"
                        disabled={methods.formState.isSubmitting}
                        className="bg-emerald-500 hover:bg-emerald-600"
                      >
                        {methods.formState.isSubmitting ? "Submitting..." : "Submit"}
                      </Button>
                    )}
                  </div>
                </div>
              </form>
            </FormProvider>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
